import {
  useForm,
  type FieldValues,
  type UseFormProps,
  type UseFormReturn,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type * as z from 'zod';

/**
 * `useForm` with the Zod schema as the single source of truth: the resolver is
 * wired in, field values are typed as the schema **input** and the submit
 * handler receives the parsed **output**, so `.default()`/`.transform()`
 * refinements flow through the types end to end.
 *
 * Validates on blur first, then on change once a field has an error
 * (`mode: 'onTouched'`) — override via `options` if a form needs otherwise.
 */
export function useZodForm<
  TInput extends FieldValues,
  TOutput extends FieldValues,
  TContext = unknown,
>(
  schema: z.ZodType<TOutput, TInput>,
  options?: Omit<UseFormProps<TInput, TContext, TOutput>, 'resolver'>,
): UseFormReturn<TInput, TContext, TOutput> {
  return useForm<TInput, TContext, TOutput>({
    mode: 'onTouched',
    ...options,
    resolver: zodResolver<TInput, TContext, TOutput>(schema),
  });
}
